import React, { useEffect } from 'react';
import { Text, Box } from 'rebass';
import { Label } from '@rebass/forms';
import store from 'store';
import { Formik } from 'formik';
import * as Yup from 'yup';
import useSWR from 'swr';
import { useWindowSize } from 'react-use';
import Grid from '@/components/app/Grid';
import { CelebrationList } from '@/components/CelebrationList/CelebrationListContext';
import { labelSx, Wrapper } from '@/components/Printables/ReusableComponents';
import { checkIfIdExist } from '@/lib/helper';
import { CelebrationLayoutFooter } from '@/components/CreateCelebrationLayout';
import { useSubmitCelebration } from '@/components/CelebrationForm/hooks';
import { videoGuestBookTemplateId, VIDEO_GUEST_BOOK } from '@/lib/constants';
import { useMobileCheckHook } from '@/hooks/celebration';
import useGetCelebrationTypes from '@/hooks/celebrationtypes';
import CelebrationTemplateBox from '@/components/CelebrationTemplateBox';
import { CelebrationFormList } from '@/components/CelebrationForm/CelebrationFormContext';

function SelectTemplatePage({
  celebration,
  moveToSpecificStep,
  onBack,
  setIsTemplateSelected,
  isTemplateSelected,
}) {
  const isFromMobile = useMobileCheckHook();
  const { width } = useWindowSize();

  const submitCelebration = useSubmitCelebration();
  const { mutate: revalidateCelebration } = useSWR(
    checkIfIdExist(celebration?.id) ? `/v3/celebrations/${celebration?.id}?shallow=true` : null
  );

  useEffect(() => {
    if (isTemplateSelected) {
      if (celebration?.theme === VIDEO_GUEST_BOOK) moveToSpecificStep(6);
      else moveToSpecificStep(4);
    }
  }, [isTemplateSelected]);

  const listOfCelebrationTypes = useGetCelebrationTypes();

  const onSubmitPress = async (attrs, { setSubmitting }) => {
    if (attrs['celebration-template-id'] !== celebration['celebration-template']?.id) {
      setSubmitting(true);
      const attributes = attrs;
      await submitCelebration(attributes, celebration?.id);
      revalidateCelebration(true);
      setSubmitting(false);
    }
    if (attrs['celebration-template-id'] === videoGuestBookTemplateId) moveToSpecificStep(6);
    else moveToSpecificStep(4);
    store.set('new-celebration-template-selected', true);
    setIsTemplateSelected(true);
  };

  const newCelebrationSchema = Yup.object().shape({
    'celebration-template-id': Yup.mixed().nullable(),
  });
  if (isTemplateSelected) return null;

  let gridGap = 4;
  if (isFromMobile) {
    if (width < 500) gridGap = 1;
    else gridGap = 2;
  }

  return (
    <Box pb={130}>
      <CelebrationFormList>
        <Formik
          validationSchema={newCelebrationSchema}
          initialValues={{
            'celebration-template-id': '',
          }}
          enableReinitialize
          onSubmit={onSubmitPress}
        >
          {({ values, handleSubmit, isSubmitting, setFieldValue }) => (
            <Wrapper style={{ padding: 0, marginBottom: 0 }}>
              <Label
                htmlFor="due-date"
                justifyContent="center"
                pb={1}
                sx={labelSx}
                color="#505050"
                textAlign="center"
                mt={['18px', '30px']}
              >
                Choose a video style for {celebration?.['for-whom']}.
              </Label>
              <Text color="#8E8E8E" fontWeight={400} fontSize={['16px', '20px']} lineHeight="140%">
                We have 3 different Celebration types, shown below. Select the one you’d like for
                this event. If you later change your mind, simply create a new celebration using a
                different type.
              </Text>
              <Grid
                py={isFromMobile ? 2 : 5}
                gridGap={gridGap}
                justifyContent="center"
                gridTemplateColumns={
                  isFromMobile
                    ? 'repeat(auto-fill, minmax(100vw, 100vw))'
                    : 'repeat(auto-fill, minmax(390px, 25vw))'
                }
              >
                {listOfCelebrationTypes !== null &&
                  listOfCelebrationTypes.map((attributes) => (
                    <CelebrationList>
                      <CelebrationTemplateBox
                        isFromMobile={isFromMobile}
                        id={`${attributes?.templateId}-list-item`}
                        {...attributes}
                        showLearnMore
                        isSelected={values['celebration-template-id'] === attributes?.templateId}
                        onClick={() => {
                          if (values['celebration-template-id'] !== attributes?.templateId) {
                            setFieldValue('celebration-template-id', attributes?.templateId);
                          } else {
                            setFieldValue('celebration-template-id', '');
                          }
                        }}
                      />
                    </CelebrationList>
                  ))}
              </Grid>
              {/* </Grid> */}
              <CelebrationLayoutFooter
                onNextPress={handleSubmit}
                disabled={!values['celebration-template-id'] || isSubmitting}
                onBack={onBack}
                type="submit"
                nextButtonText="Continue"
                sx={{ left: 0 }}
                notChangeable
              />
            </Wrapper>
          )}
        </Formik>
      </CelebrationFormList>
    </Box>
  );
}

export default SelectTemplatePage;
